/* eslint-disable @typescript-eslint/no-explicit-any */
import { Suspense, lazy } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "@/components/shared/ui/sonner";
import { Toaster as ShadcnToaster } from "@/components/shared/ui/toaster";
import { AuthProvider } from "@/hooks/useAuth";
import { SecurityProvider } from "@/contexts/SecurityContext";
import { RealtimeErrorBoundary } from "@/components/shared/common/RealtimeErrorBoundary";
import { I18nextProvider } from "react-i18next";
import i18n from "@/i18n/i18n";
import { ProtectedRoute } from "@/components/shared/auth/ProtectedRoute";
import { PublicRoute } from "@/components/shared/auth/PublicRoute";
import { AppLayout } from "@/components/shared/layout/AppLayout";
import { ThemeProvider } from "@/components/shared/ui/theme-provider";
import "./App.css";

// Lazy load components for better performance
const Index = lazy(() => import("./pages/Index"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const NotFound = lazy(() => import("./pages/NotFound"));
const LoginForm = lazy(() => import("@/components/shared/auth/LoginForm"));
const UserProfileSettings = lazy(
  () => import("@/components/client/profile/UserProfileSettings")
);
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const SetupPassword = lazy(() => import("@/pages/SetupPassword"));
const Legal = lazy(() => import("./pages/Legal"));
const TermsPublic = lazy(() => import("./pages/TermsPublic"));
const PrivacyPublic = lazy(() => import("./pages/PrivacyPublic"));
const AdminLegal = lazy(() => import("./pages/AdminLegal"));
// Deep-linkable dashboards and content routes
const TherapistDashboard = lazy(() =>
  import("@/components/therapist/dashboard/TherapistDashboard").then((m) => ({
    default: m.TherapistDashboard,
  }))
);
const ClientDashboard = lazy(() =>
  import("@/components/client/dashboard/ClientDashboard").then((m) => ({
    default: m.ClientDashboard,
  }))
);
const ClientJourneyHistory = lazy(() =>
  import("@/components/client/journey/ClientJourneyHistory").then((m) => ({
    default: m.ClientJourneyHistory,
  }))
);
const ContentLibrary = lazy(
  () => import("@/components/therapist/content/ContentLibrary")
);
const ClientWorkspace = lazy(() =>
  import("@/components/therapist/client-workspace/ClientWorkspace").then((m) => ({
    default: m.ClientWorkspace,
  }))
);

// Create a client
// OPTIMIZATION: Longer staleTime in dev to reduce refetch frequency and CPU usage
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: import.meta.env.DEV ? 1000 * 60 * 15 : 1000 * 60 * 5, // 15 min dev, 5 min prod
      retry: (failureCount, error: any) => {
        // Don't retry on authentication errors
        if (error?.status === 401) return false;
        return failureCount < 3;
      },
    },
  },
});

function App() {
  return (
    <RealtimeErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <SecurityProvider>
                <Router>
                  <Suspense
                    fallback={
                      <div className="flex items-center justify-center min-h-screen">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                      </div>
                    }
                  >
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <PublicRoute>
                            <AppLayout>
                              <Index />
                            </AppLayout>
                          </PublicRoute>
                        }
                      />
                      <Route
                        path="/login"
                        element={
                          <PublicRoute>
                            <AppLayout>
                              <LoginForm />
                            </AppLayout>
                          </PublicRoute>
                        }
                      />
                      <Route
                        path="/reset-password"
                        element={
                          <PublicRoute>
                            <ResetPassword />
                          </PublicRoute>
                        }
                      />
                      <Route
                        path="/setup-password"
                        element={<SetupPassword />}
                      />
                      {/* Backward-compatible dashboard entry (role-aware redirect in pages/Dashboard) */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <Dashboard />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Terms (legal acceptance) page (no app shell) */}
                      <Route
                        path="/terms"
                        element={
                          <ProtectedRoute>
                            <Legal />
                          </ProtectedRoute>
                        }
                      />

                      {/* Public, deep-linkable legal pages (Danish paths) */}
                      <Route
                        path="/betingelser"
                        element={
                          <AppLayout>
                            <TermsPublic />
                          </AppLayout>
                        }
                      />
                      <Route
                        path="/privatlivspolitik"
                        element={
                          <AppLayout>
                            <PrivacyPublic />
                          </AppLayout>
                        }
                      />

                      {/* Admin index redirects to legal admin for now */}
                      <Route
                        path="/admin"
                        element={<Navigate to="/admin/legal" replace />}
                      />
                      {/* Client deep links */}
                      <Route
                        path="/dashboard/client"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <Navigate
                                to="/dashboard/client/journey"
                                replace
                              />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/client/journey"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <ClientDashboard />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/client/journey/history"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <ClientJourneyHistory />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />

                      {/* Therapist deep links */}
                      <Route
                        path="/dashboard/therapist"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <Navigate
                                to="/dashboard/therapist/journey-builder"
                                replace
                              />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/therapist/journey-builder"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <TherapistDashboard />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/therapist/journey-builder/:journeyId"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <TherapistDashboard />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/therapist/clients"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <TherapistDashboard />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      {/* Deprecated: Redirect old client-notes route to workspace conversations */}
                      <Route
                        path="/dashboard/therapist/client-notes/:clientId"
                        element={
                          <ProtectedRoute>
                            <Navigate to="/dashboard/therapist/clients" replace />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/therapist/client/:clientId/workspace"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <ClientWorkspace />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/therapist/security-audit"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <TherapistDashboard />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/therapist/content"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <ContentLibrary />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      {/* Admin - Legal management */}
                      <Route
                        path="/admin/legal"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <AdminLegal />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/profile"
                        element={
                          <ProtectedRoute>
                            <AppLayout>
                              <UserProfileSettings />
                            </AppLayout>
                          </ProtectedRoute>
                        }
                      />
                      <Route path="/404" element={<NotFound />} />
                      <Route
                        path="*"
                        element={<Navigate to="/404" replace />}
                      />
                    </Routes>
                  </Suspense>
                </Router>
              </SecurityProvider>
            </AuthProvider>
            <Toaster />
            <ShadcnToaster />
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </ThemeProvider>
      </I18nextProvider>
    </RealtimeErrorBoundary>
  );
}

export default App;
